import React from 'react';
import {Modal, View, StyleSheet, ViewStyle, TextStyle} from 'react-native';
import {Text, TextInput, MultiSelect, DatePicker, Button} from 'ui-kits';
import colors from 'theme/Colors';
import {spacing, corner_radius} from 'theme/Units';
import {Formik} from 'formik';
import {QuickAddUserStoryValidation} from 'utils/schema';
import {selectedProjectTeam} from 'redux/selectors/projectSelector';
import {useSelector} from 'react-redux';
import {projectTeamMember} from 'redux/slices/projectSlice';
import {Priority} from '../Priority';

type fieldValues = {
  title: string;
  due_date: Date | null;
  responsible: projectTeamMember[];
  priority: number;
};

type propType = Readonly<{
  visible: boolean;
  onSave: (values: fieldValues) => void;
  onCancel: () => void;
}>;

const initialValues: fieldValues = {
  title: '',
  due_date: null,
  responsible: [],
  priority: 0,
};

export function QuickUserStoryAddModal(props: propType) {
  const {onCancel, onSave, visible} = props;
  const currentTeam = useSelector(selectedProjectTeam);

  function renderItem(item: projectTeamMember) {
    return <Text>{item?.first_name} {item?.last_name}</Text>;
  }
  const responsibleDisplayitem = (item: projectTeamMember[]) => (
    <Text>{item[0]?.first_name} {item[0]?.last_name}</Text>
  );

  return (
    <Modal
      animationType="none"
      onRequestClose={onCancel}
      visible={visible}
      transparent>
      <View style={styles.modalContainer}>
        <View style={styles.modalContent}>
          <Text style={styles.formHeading}>Quick Add</Text>
          <Formik
            initialValues={initialValues}
            validationSchema={QuickAddUserStoryValidation}
            onSubmit={onSave}>
            {({
              handleChange,
              handleBlur,
              handleSubmit,
              values,
              errors,
              setFieldValue,
              dirty,
              isValid,
            }) => {
              return (
                <View>
                  <View style={styles.form}>
                    <TextInput
                      placeholder="Enter Project Name"
                      label="Title"
                      required
                      maxLength={50}
                      value={values.title}
                      error={errors.title}
                      onChangeText={handleChange('title')}
                      onBlur={handleBlur('title')}
                    />

                    <DatePicker
                      label="Due Date"
                      value={values.due_date}
                      error={errors.due_date}
                      calenderStyle={styles.dueDateCallender}
                      onChangeDate={date => setFieldValue('due_date', date)}
                    />

                    <MultiSelect
                      label="Responsible"
                      keyExtractor={(item: projectTeamMember) =>
                        item.id.toString()
                      }
                      options={currentTeam ?? []}
                      searchKeyExtractor={(item: projectTeamMember) =>
                        item.first_name + item.last_name
                      }
                      renderItem={renderItem}
                      onSave={selected => {
                        setFieldValue('responsible', selected);
                      }}
                      displayItem={responsibleDisplayitem}
                      singleSelect={true}
                      selectedEntries={values.responsible}
                      searchPlaceholder="Search..."
                      placeholder="Add Member"
                    />

                    <Priority
                      priority={values.priority}
                      onChange={priority => {
                        setFieldValue('priority', priority);
                      }}
                      mode="edit"
                      label="Priority"
                    />
                  </View>
                  <View style={styles.actionButonWrapper}>
                    <Button
                      onPress={onCancel}
                      style={styles.actionButton}
                      text="Cancel"
                      variant="secondary"
                    />
                    <Button
                      onPress={handleSubmit}
                      style={styles.actionButton}
                      text="Add"
                      variant="primary"
                      disabled={!dirty || !isValid}
                    />
                  </View>
                </View>
              );
            }}
          </Formik>
        </View>
      </View>
    </Modal>
  );
}

type styleType = {
  modalContainer: ViewStyle;
  modalContent: ViewStyle;
  form: ViewStyle;
  formHeading: TextStyle;
  actionButton: ViewStyle;
  actionButonWrapper: ViewStyle;
  dueDateCallender: ViewStyle;
};
const styles: styleType = StyleSheet.create({
  dueDateCallender: {
    top:0,
    right:0,
  },
  modalContent: {
    backgroundColor: colors.palette.white,
    padding: spacing['8'],
    borderRadius: corner_radius['rounded-lg'],
    gap: spacing['4'],
    boxSizing: 'border-box',
    maxWidth: 500,
  },
  actionButonWrapper: {
    marginTop: spacing['2'],
    flexDirection: 'row',
    gap: spacing['4'],
    justifyContent: 'center',
    zIndex: -1,
    width: '100%',
  },
  actionButton: {
    width: 96,
    paddingHorizontal: spacing['4'],
  },
  modalContainer: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    padding: spacing['8'],
    justifyContent: 'center',
    alignItems: 'center',
  },
  form: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    columnGap: spacing['4'],
    rowGap: spacing['2'],
  },
  formHeading: {
    fontSize: 20,
    fontWeight: 700,
  },
});
